'use client';

import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { getWithAuth } from '../lib/api-client';
import useSWR, { mutate } from 'swr';

interface SubscriptionDetails {
  status: string;
  currentPeriodEnd: string;
  plan: string | null;
}

interface SubscriptionResponse {
  hasSubscription: boolean;
  subscriptionDetails: SubscriptionDetails | null;
  timestamp: string;
}

/**
 * Hook to check and manage user subscription status
 */
export function useSubscription() {
  const router = useRouter();
  const supabase = createClient();

  // Get session data
  const { data: session } = useSWR('supabase-session', async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  });

  // Get subscription data
  const { data, error, isLoading } = useSWR<SubscriptionResponse>(
    session ? '/api/user/subscription' : null,
    async () => {
      try {
        return await getWithAuth<SubscriptionResponse>('/api/user/subscription');
      } catch (error: any) {
        console.error('Error fetching subscription:', error);
        throw error;
      }
    },
    {
      refreshInterval: 5 * 60 * 1000, // Refresh every 5 minutes instead of 1 minute
      revalidateOnFocus: false, // Don't revalidate on focus to reduce requests
      revalidateOnReconnect: true
    }
  );

  const redirectToCheckout = (plan: 'monthly' | 'annual') => {
    window.location.href = `/api/integrations/stripe/checkout/${plan}`;
  };

  const redirectToBilling = async () => {
    try {
      const response = await fetch('/api/integrations/stripe/billing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // If user doesn't have billing yet, redirect to pricing
        if (response.status === 404) {
          router.push('/home#pricing');
          return;
        }
        throw new Error(data.message || 'Failed to access billing portal');
      }
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No billing portal URL returned');
      }
    } catch (error) {
      console.error('Error accessing billing portal:', error);
      throw error;
    }
  };

  const getClientSecrets = async () => {
    try {
      const response = await fetch('/api/integrations/stripe/setup-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to get client secrets');
      }
      
      return data.clientSecrets;
    } catch (error) {
      console.error('Error getting client secrets:', error);
      throw error;
    }
  };

  const refresh = async () => {
    await mutate('/api/user/subscription');
  };

  return {
    hasSubscription: data?.hasSubscription ?? false,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Unknown error') : null,
    subscriptionDetails: data?.subscriptionDetails ?? null,
    lastUpdatedAt: data?.timestamp ?? null,
    redirectToCheckout,
    redirectToBilling,
    getClientSecrets,
    refresh
  };
} 